import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { ResponsiveBar } from '@nivo/bar'
import { useFormattingContext } from '../FormattingProvider/FormattingContext'
import { useFirmReportData } from '../../../api/coreData'
import { useDenaliFilter } from './filters/DenaliFilterContextProvider'

const defaultAxisLeft = {
  tickSize: 5,
  tickValues: 4,
  tickPadding: 15,
  tickRotation: 0,
  truncateTickAt: 0
}

const defaultAxisBottom = {
}

const applyStyleVariant = (chartProps, styleVariant) => {
  const result = { ...(chartProps || {}) }

  if (styleVariant === 'opl') {
  }

  return result
}

const useFormatters = (valueFormat, labelFormat, indexFormat, layout) => {
  const { formatter } = useFormattingContext()

  const valueFormatter = useCallback(d => {
    return formatter(d.value, valueFormat)
  }, [formatter, valueFormat])

  const labelFormatter = useCallback(d => {
    return formatter(d, labelFormat)
  }, [formatter, labelFormat])

  const indexFormatter = useCallback(d => {
    return formatter(d, indexFormat)
  }, [formatter, indexFormat])

  const defaultFormatter = useCallback(d => {
    return formatter(d, valueFormat)
  }, [formatter, valueFormat])

  return {
    valueFormatter,
    labelFormatter,
    defaultFormatter,
    formatLeft: layout === 'vertical' ? labelFormatter : indexFormatter,
    formatBottom: layout === 'horizontal' ? labelFormatter : indexFormatter
  }
}

function DenaliBarChart ({
  reportId,
  baseQuery,
  indexBy,
  keys,
  valueFormat,
  labelFormat,
  indexFormat,
  height,
  layout,
  keyColors,
  styleVariant,
  ...chartProps
}) {
  const { filter, membershipFilter } = useDenaliFilter()
  const query = useMemo(() => {
    return {
      ...baseQuery,
      membershipFilter,
      filters: {
        ...(filter || {}),
        ...(baseQuery?.filters || {})
      },
      reportId
    }
  }, [reportId, baseQuery, filter, membershipFilter])

  const { data: report, isLoading } = useFirmReportData(query)
  const { valueFormatter, formatLeft, formatBottom, defaultFormatter } = useFormatters(valueFormat, labelFormat, indexFormat, layout)
  const _keys = useMemo(() => {
    if (keys !== '*') {
      return keys
    }
    const result = (report?.data || []).reduce((acc, item) => {
      const keys = Object.keys(item).filter(f => f !== indexBy)
      keys.forEach(acc.add, acc)

      return acc
    }, new Set())

    return Array.from(result)
  }, [report, keys, indexBy])
  const _restProps = useMemo(() => applyStyleVariant(chartProps, styleVariant), [chartProps, styleVariant])

  if (isLoading) return null

  return (
    <div style={{ height }}>
      <ResponsiveBar
        theme={{
          text: {
            fontFamily: 'var(--font-sans) !important',
            fontSize: '12px',
            color: 'var(--text-color-main)'
          }
        }}
        data={report?.data || []}
        indexBy={indexBy}
        keys={_keys}
        label={valueFormatter}
        valueFormat={defaultFormatter}
        colors={keyColors ? d => keyColors[d.id] : undefined}
        layout={layout}
        xScale={{
          type: 'time',
          format: '%Y-%m-%d',
          precision: 'day'
        }}
        {..._restProps}
        axisLeft={{
          ...(chartProps.axisLeft || defaultAxisLeft),
          format: formatLeft
        }}
        axisBottom={{
          ...(chartProps.axisBottom || defaultAxisBottom),
          format: formatBottom
        }}
      />
    </div>
  )
}

DenaliBarChart.propTypes = {
  reportId: PropTypes.string,
  baseQuery: PropTypes.object,
  indexBy: PropTypes.string,
  keys: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  valueFormat: PropTypes.string,
  labelFormat: PropTypes.string,
  indexFormat: PropTypes.string,
  layout: PropTypes.oneOf(['horizontal', 'vertical']),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  keyColors: PropTypes.objectOf(PropTypes.string),
  styleVariant: PropTypes.oneOf(['opl', 'none'])
}

DenaliBarChart.defaultProps = {
  valueFormat: 'marketValue',
  labelFormat: 'human',
  indexFormat: 'title',
  layout: 'vertical',
  height: 300
}

export default DenaliBarChart
